import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { BsCheckCircleFill, BsExclamationCircleFill } from "react-icons/bs";
import Label from "../../../../../../components/form_fields/Label";
import {
	useGetMerchantCategories,
	useGetPaymentCompanyTypes,
	useGetPaymentTiers,
	useVerifyMerchantDetails,
} from "../../../../../../services/miscellaneous-payment.service";
import { VerifyMerchantDetailsDto } from "../../../../../../models/miscellaneous.dto";
import { FieldError, FileUploadField, SectionHeading } from "../components";
import {
	MerchantCategory,
	PaymentSetupFormValues,
	PaymentTier,
	getTierRange,
	inputClassName,
	splitCategories,
	toList,
	toRecord,
} from "../helpers";

type AvailabilityResult = {
	field: string;
	available: boolean;
	message: string;
};

const BusinessInfoStep = ({ readOnlyIdentity }: { readOnlyIdentity?: boolean }) => {
	const {
		register,
		control,
		watch,
		formState: { errors },
	} = useFormContext<PaymentSetupFormValues>();

	const country = watch("country");
	const { data: tiersResponse } = useGetPaymentTiers();
	const { data: companyTypesResponse } = useGetPaymentCompanyTypes();
	const { data: categoriesResponse } = useGetMerchantCategories({ country });

	const tiers = toList<PaymentTier>(tiersResponse);
	const companyTypes = toList<{ id?: string; name: string; description?: string }>(
		companyTypesResponse,
	);
	const categories = toList<MerchantCategory>(categoriesResponse);
	const selectedTier = tiers.find((tier) => tier.name === watch("tier"));

	const { mutate: verifyMerchantDetails, isLoading: checkingAvailability } =
		useVerifyMerchantDetails();
	const [availability, setAvailability] = useState<AvailabilityResult[]>();

	const checkAvailability = () => {
		const [companyName, alias, taxIdentificationNumber] = watch([
			"companyName",
			"alias",
			"taxIdentificationNumber",
		]);

		// The provider accepts any subset of the three, so only send what's filled in
		const payload = Object.fromEntries(
			Object.entries({ companyName, alias, taxIdentificationNumber }).filter(
				([, value]) => !!value?.trim(),
			),
		) as unknown as VerifyMerchantDetailsDto;

		if (!Object.keys(payload).length) return;

		verifyMerchantDetails(
			{ payload },
			{
				onSuccess: (response) =>
					setAvailability(toRecord<{ results: AvailabilityResult[] }>(response)?.results || []),
			},
		);
	};

	return (
		<div>
			<SectionHeading
				title="Business information"
				description="Enter the company's details exactly as they appear on its registration documents."
			/>
			<div className="grid grid-cols-2 gap-x-6 gap-y-5">
				<div className="col-span-2">
					<Label for="companyName" title="Registered company name" />
					<input
						id="companyName"
						placeholder="e.g. Cedar Coast Trading Limited"
						disabled={readOnlyIdentity}
						{...register("companyName", {
							required: "Enter the registered company name",
							maxLength: { value: 100, message: "Use 100 characters or fewer" },
						})}
						className={inputClassName(!!errors.companyName)}
					/>
					<FieldError error={errors.companyName} />
				</div>
				<div>
					<Label for="tradeName" title="Trading name" optional />
					<input
						id="tradeName"
						placeholder="If different from the registered name"
						{...register("tradeName", {
							maxLength: { value: 100, message: "Use 100 characters or fewer" },
						})}
						className={inputClassName(!!errors.tradeName)}
					/>
					<FieldError error={errors.tradeName} />
				</div>
				<div>
					<Label for="alias" title="Alias" optional />
					<input
						id="alias"
						placeholder="Short code, max 8 characters"
						disabled={readOnlyIdentity}
						{...register("alias", {
							maxLength: { value: 8, message: "Use 8 characters or fewer" },
						})}
						className={inputClassName(!!errors.alias)}
					/>
					<FieldError error={errors.alias} />
				</div>
				<div>
					<Label for="taxIdentificationNumber" title="Tax identification number (TIN)" optional />
					<input
						id="taxIdentificationNumber"
						placeholder="Enter TIN"
						disabled={readOnlyIdentity}
						{...register("taxIdentificationNumber", {
							validate: (value) =>
								!value ||
								(value.length >= 10 && value.length <= 20) ||
								"TIN must be 10 to 20 characters",
						})}
						className={inputClassName(!!errors.taxIdentificationNumber)}
					/>
					<FieldError error={errors.taxIdentificationNumber} />
				</div>
				<div className="flex items-end">
					{!readOnlyIdentity && (
						<button
							type="button"
							onClick={checkAvailability}
							disabled={checkingAvailability}
							className="h-14 px-5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
						>
							{checkingAvailability ? "Checking..." : "Check name, alias & TIN availability"}
						</button>
					)}
				</div>
				{availability && availability.length > 0 && (
					<div className="col-span-2 rounded-lg border border-gray-200 divide-y text-xs">
						{availability.map((result) => (
							<div key={result.field} className="flex items-start gap-2 px-4 py-3">
								{result.available ? (
									<BsCheckCircleFill className="text-green-600 mt-0.5 shrink-0" />
								) : (
									<BsExclamationCircleFill className="text-red-500 mt-0.5 shrink-0" />
								)}
								<span className="text-gray-700">{result.message}</span>
							</div>
						))}
					</div>
				)}

				<div>
					<Label for="tier" title="Tier" />
					<select
						id="tier"
						{...register("tier", { required: "Select a tier" })}
						className={inputClassName(!!errors.tier)}
					>
						<option value="">Select tier</option>
						{tiers.map((tier) => (
							<option key={tier.name} value={tier.name}>
								{tier.name}
								{getTierRange(tier) ? ` (${getTierRange(tier)})` : ""}
							</option>
						))}
					</select>
					<p className="text-[11px] text-gray-500 mt-1">
						{selectedTier
							? `Transaction limit ${getTierRange(selectedTier) || selectedTier.description}. `
							: "Choose the tier that matches the amounts the company will transact. "}
						The tier also decides which documents are required.
					</p>
					<FieldError error={errors.tier} />
				</div>
				<div>
					<Label for="country" title="Country of registration" />
					<input
						id="country"
						disabled
						{...register("country", { required: "Country is required" })}
						className={inputClassName(!!errors.country)}
					/>
					<FieldError error={errors.country} />
				</div>
				<div className="col-span-2">
					<Label for="companyCategories" title="Business categories" />
					<Controller
						name="companyCategories"
						control={control}
						rules={{ required: "Select at least one business category" }}
						render={({ field }) => {
							const selectedNames = splitCategories(field.value);
							const selected = categories.filter((category) =>
								selectedNames.includes(category.name),
							);
							return (
								<Autocomplete
									multiple
									id="companyCategories"
									options={categories}
									value={selected}
									getOptionLabel={(option) => option.label || option.name}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									onChange={(_, value) =>
										field.onChange(value.map((category) => category.name).join(","))
									}
									onBlur={field.onBlur}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												{...getTagProps({ index })}
												key={option.id}
												size="small"
												label={option.label || option.name}
											/>
										))
									}
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder={selected.length ? "" : "Search categories"}
											error={!!errors.companyCategories}
										/>
									)}
									sx={{
										mt: 1,
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#F2F2F4",
											borderRadius: "8px",
											minHeight: "56px",
											fontSize: "14px",
										},
										"& .MuiOutlinedInput-notchedOutline": {
											border: errors.companyCategories ? undefined : "none",
										},
									}}
								/>
							);
						}}
					/>
					<FieldError error={errors.companyCategories} />
				</div>
				<div>
					<Label for="countryMerchantCategoryId" title="Regulated business classification" optional />
					<select
						id="countryMerchantCategoryId"
						{...register("countryMerchantCategoryId")}
						className={inputClassName()}
					>
						<option value="">Select classification</option>
						{/* The backend expects the selected type's id here */}
						{companyTypes
							.filter((type) => type.id != null)
							.map((type) => (
								<option key={type.id} value={type.id}>
									{type.name}
								</option>
							))}
					</select>
				</div>
				<div>
					<Label for="typeOfCompany" title="Type of company" optional />
					<select
						id="typeOfCompany"
						{...register("typeOfCompany")}
						className={inputClassName()}
					>
						<option value="">Select type</option>
						{companyTypes.map((type) => (
							<option key={type.name} value={type.name}>
								{type.name}
							</option>
						))}
					</select>
				</div>
				<div>
					<Label for="companyRegistrationNumber" title="Company registration number" optional />
					<input
						id="companyRegistrationNumber"
						placeholder="e.g. CS123452021"
						{...register("companyRegistrationNumber", {
							maxLength: { value: 20, message: "Use 20 characters or fewer" },
						})}
						className={inputClassName(!!errors.companyRegistrationNumber)}
					/>
					<FieldError error={errors.companyRegistrationNumber} />
				</div>
				<div>
					<Label for="vatRegistrationNumber" title="VAT registration number" optional />
					<input
						id="vatRegistrationNumber"
						placeholder="Enter VAT number"
						{...register("vatRegistrationNumber", {
							maxLength: { value: 20, message: "Use 20 characters or fewer" },
						})}
						className={inputClassName(!!errors.vatRegistrationNumber)}
					/>
					<FieldError error={errors.vatRegistrationNumber} />
				</div>
				<div>
					<Label for="dateOfIncorporation" title="Date of incorporation" optional />
					<input
						id="dateOfIncorporation"
						type="date"
						{...register("dateOfIncorporation")}
						className={inputClassName()}
					/>
				</div>
				<div>
					<Label for="dateOfCommencement" title="Date business started" optional />
					<input
						id="dateOfCommencement"
						type="date"
						{...register("dateOfCommencement")}
						className={inputClassName()}
					/>
				</div>
				<div className="col-span-2">
					<Label for="companyLogo" title="Company logo" />
					<Controller
						name="companyLogo"
						control={control}
						rules={{ required: "Upload the company logo" }}
						render={({ field }) => (
							<FileUploadField
								value={field.value}
								onChange={field.onChange}
								accept=".png,.jpg,.jpeg,.svg"
								buttonLabel="Upload logo (PNG, JPG or SVG)"
								hasError={!!errors.companyLogo}
							/>
						)}
					/>
					<FieldError error={errors.companyLogo} />
				</div>
			</div>
		</div>
	);
};

export default BusinessInfoStep;
